/**
 * Page-wide coordinator (PRD §6, C7, C29): one playback token, float ownership and
 * partner-player detection. No player decides for itself.
 */

/**
 * @typedef {'video'|'display'|'retry'|'click-to-play'} TokenDecision
 */

/**
 * Create the coordinator singleton.
 * @param {object} options
 * @param {number} options.pendingWaitMs §6 rule 3 wait cap (3000)
 * @param {Function} options.setTimeoutFn
 * @param {Function} options.clearTimeoutFn
 * @param {Function} [options.onEvent] (name, params) telemetry/debug sink
 * @returns {object}
 */
export function createCoordinator({ pendingWaitMs, setTimeoutFn, clearTimeoutFn, onEvent = () => {} }) {
  const token = { state: 'free', holder: null };
  const players = new Map(); // id -> { yieldToken(reason) }
  const waiters = []; // FIFO of { id, wake }
  let partnerBlocked = false;
  let floatOwner = null;

  /**
   * @param {string} action qsvp_token action
   * @param {string} playerId
   */
  function emit(action, playerId) {
    onEvent('qsvp_token', { action, player_id: playerId });
  }

  /** Wake the first waiter, if any (§6: the first slot to start waiting is first in line). */
  function wakeFirst() {
    if (waiters.length) waiters[0].wake();
  }

  /** Wake every waiter (holder resolved as filled: they all go to display). */
  function wakeAll() {
    waiters.slice().forEach((waiter) => waiter.wake());
  }

  /**
   * Remove a waiter from the queue.
   * @param {object} waiter
   */
  function leaveQueue(waiter) {
    const index = waiters.indexOf(waiter);
    if (index >= 0) waiters.splice(index, 1);
  }

  return {
    token,
    /** @returns {boolean} */
    isPartnerBlocked: () => partnerBlocked,
    /** @returns {string|null} */
    getFloatOwner: () => floatOwner,
    /**
     * Register a player so the coordinator can make it yield.
     * @param {string} id
     * @param {{yieldToken:Function}} handle
     */
    register(id, handle) {
      players.set(id, handle);
    },

    /**
     * Unregister on destroy (SPA navigation, §9.1).
     * @param {string} id
     */
    unregister(id) {
      players.delete(id);
      if (token.holder === id) this.release(id, 'destroyed');
      if (floatOwner === id) floatOwner = null;
    },

    /**
     * A slot reached 50% visible and wants to autoplay (§6 rules 1-3).
     * @param {string} id
     * @param {Function} isStillVisible re-checked when a wait ends
     * @returns {Promise<TokenDecision>}
     */
    acquire(id, isStillVisible) {
      // §6 / §12: a partner player present -> our players never take the token, click-to-play only.
      if (partnerBlocked) return Promise.resolve('click-to-play');
      if (token.holder === id) return Promise.resolve('video');
      if (token.state === 'free') {
        token.state = 'pending';
        token.holder = id;
        emit('take', id);
        return Promise.resolve('video');
      }
      if (token.state === 'active') return Promise.resolve('display'); // §6 rule 3: holder active
      // Pending: wait until the holder's request resolves or pendingWaitMs passes.
      return new Promise((resolve) => {
        let timer = null;
        const waiter = {
          id,
          wake: () => {
            clearTimeoutFn(timer);
            decide(false);
          },
        };
        const decide = (timedOut) => {
          leaveQueue(waiter);
          if (partnerBlocked) return resolve('click-to-play');
          // Scrolled out of view: no request, leave the queue, decide again later (no off-screen impression).
          if (!isStillVisible()) return resolve('retry');
          if (token.state === 'free') {
            token.state = 'pending';
            token.holder = id;
            emit('take', id);
            return resolve('video');
          }
          // Holder filled, or still pending at the cap -> display.
          resolve('display');
        };
        waiters.push(waiter);
        emit('wait', id);
        timer = setTimeoutFn(() => decide(true), pendingWaitMs);
      });
    },

    /**
     * The holder's ad filled and is playing (token active).
     * @param {string} id
     */
    markActive(id) {
      if (token.holder !== id) return;
      token.state = 'active';
      wakeAll(); // §6: holder filled -> waiting slots go to display
    },

    /**
     * Release the token (§6: playlist finished, auto-advance stopped, closed, first ad empty).
     * @param {string} id
     * @param {string} reason
     */
    release(id, reason) {
      if (token.holder !== id) return;
      token.state = 'free';
      token.holder = null;
      if (floatOwner === id) floatOwner = null;
      emit('release', id);
      wakeFirst(); // A waiting slot gets it first.
    },

    /**
     * A user tap on "Watch video" / "Watch next" always takes the token (§6).
     * The previous holder pauses and un-floats.
     * @param {string} id
     */
    takeForUser(id) {
      const previous = token.holder;
      if (previous && previous !== id) {
        const handle = players.get(previous);
        if (handle) handle.yieldToken('user_tap');
        if (floatOwner === previous) floatOwner = null;
      }
      token.holder = id;
      token.state = 'pending';
      emit('user_tap', id);
    },

    /**
     * C10/§6: only the token holder floats, and only one float at a time.
     * @param {string} id
     * @returns {boolean}
     */
    canFloat(id) {
      return !partnerBlocked && token.holder === id && (floatOwner === null || floatOwner === id);
    },

    /**
     * @param {string|null} id the floating player, or null when nothing floats
     */
    setFloatOwner(id) {
      floatOwner = id;
    },

    /**
     * C29 / §6: another player is in view and playing (or a partner is present). Our holder pauses,
     * releases the float and the token, and every player stays click-to-play for the page view.
     * @param {string} source description for debug
     */
    partnerDetected(source) {
      if (partnerBlocked) return;
      partnerBlocked = true;
      const holder = token.holder;
      if (holder) {
        const handle = players.get(holder);
        if (handle) handle.yieldToken('partner_player');
        token.state = 'free';
        token.holder = null;
        emit('yield_partner', holder);
      }
      floatOwner = null;
      wakeAll();
    },

    /** Reset everything (SPA route change, §9.1). */
    reset() {
      token.state = 'free';
      token.holder = null;
      floatOwner = null;
      waiters.splice(0, waiters.length);
      players.clear();
      partnerBlocked = false;
    },
  };
}

/** Iframe hosts that are ads, not players (excluded from C29 detection). */
const AD_IFRAME_PATTERN = /^(google_ads_iframe|aswift_|google_esf)/;
/** Iframe sources that look like video players (C29 "any new video or player iframe"). */
const PLAYER_IFRAME_PATTERN =
  /(player|video|embed|connatix|vidcrunch|unibots|vdo\.ai|jwplayer|youtube|vimeo|dailymotion|brightcove)/i;

/**
 * True when an element is another video player (partner or unknown) and not ours.
 * @param {Element} el
 * @param {string[]} partnerSelectors
 * @param {Function} isOurs (el) => boolean
 * @returns {boolean}
 */
export function isForeignPlayer(el, partnerSelectors, isOurs) {
  if (!el || el.nodeType !== 1 || isOurs(el)) return false;
  const selector = partnerSelectors.join(',');
  if (selector && el.matches(selector)) return true;
  const tag = el.tagName;
  if (tag === 'VIDEO') return true;
  if (tag === 'IFRAME') {
    if (AD_IFRAME_PATTERN.test(el.id || '') || AD_IFRAME_PATTERN.test(el.name || '')) return false;
    return PLAYER_IFRAME_PATTERN.test(el.src || '');
  }
  return false;
}

/**
 * Start the C29 guard: MutationObserver for partner selectors and new video/player iframes,
 * plus a capture-phase "playing" listener on the document.
 * @param {object} options
 * @param {Document} options.doc
 * @param {Function} options.MutationObserverCtor
 * @param {string[]} options.partnerSelectors
 * @param {Function} options.isOurs (el) => boolean
 * @param {Function} options.isInView (el) => boolean
 * @param {Function} options.onForeignPlayer (source) => void
 * @returns {Function} stop
 */
export function startPartnerGuard({ doc, MutationObserverCtor, partnerSelectors, isOurs, isInView, onForeignPlayer }) {
  const selector = partnerSelectors.join(',');

  /**
   * Inspect an element and its subtree for partner players.
   * @param {Element} root
   */
  const inspect = (root) => {
    if (!root || root.nodeType !== 1) return;
    const candidates = [root];
    if (root.querySelectorAll) {
      candidates.push(...root.querySelectorAll(selector ? selector + ',video,iframe' : 'video,iframe'));
    }
    for (const el of candidates) {
      if (!isForeignPlayer(el, partnerSelectors, isOurs)) continue;
      const isPartner = selector && el.matches(selector);
      // A partner present on the page blocks autoplay and float (§12). Unknown video/iframes count
      // when in view; for iframes we cannot read their state, so in view is enough (C29).
      if (isPartner || ((el.tagName === 'IFRAME' || !el.paused) && isInView(el))) {
        onForeignPlayer(isPartner ? 'partner_selector' : el.tagName.toLowerCase());
        return;
      }
    }
  };

  inspect(doc.body);
  const observer = new MutationObserverCtor((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes || []) inspect(node);
    }
  });
  observer.observe(doc.body, { childList: true, subtree: true });

  const onPlaying = (event) => {
    const target = event.target;
    if (target && target.tagName === 'VIDEO' && !isOurs(target) && isInView(target)) onForeignPlayer('video_playing');
  };
  doc.addEventListener('playing', onPlaying, true);
  return () => {
    observer.disconnect();
    doc.removeEventListener('playing', onPlaying, true);
  };
}
